// frontend/src/askos/mocktest/MockTestRunner.js
//
// Timed mock-test runner with full exam-structure fidelity: compulsory
// full-questions are always shown; choice groups ("attempt any 2 of these
// 3 questions") let the student pick which full-questions to attempt,
// exactly like the real paper. Countdown is client-side only — the server
// always accepts a submission whenever POSTed, no hard cutoff (a
// deliberate scope simplification; see mocktest_router.py's docstring).
//
// Descriptive (essay) sub-parts collect only the written answer here — no
// self-score input, since the model answer is withheld until after
// submission (grading yourself blind during the test wouldn't mean
// anything). Self-assessment happens on the result page instead.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMockTestQuestions, submitMockAttempt } from '../../api/mockTestApi';

const fmtTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const SubPartInput = ({ sp, answer, onChange }) => (
    <div style={{ marginBottom: 14 }}>
        <div className="d-flex justify-content-between">
            <strong style={{ fontSize: 13 }}>({sp.sub_part}) {sp.question_text}</strong>
            <span className="empty-note">{sp.marks} marks{sp.word_limit ? ` · ${sp.word_limit} words` : ''}</span>
        </div>
        {sp.options ? (
            <div className="d-flex flex-column gap-2 mt-2">
                {Object.entries(sp.options).map(([key, text]) => (
                    <button
                        key={key}
                        type="button"
                        className={answer === key ? 'primary-btn' : 'secondary-btn'}
                        style={{ textAlign: 'left' }}
                        onClick={() => onChange(key)}
                    >
                        <strong>{key}.</strong> {text}
                    </button>
                ))}
            </div>
        ) : (
            <textarea
                className="form-control mt-2"
                rows={4}
                placeholder="Write your answer…"
                value={(answer && answer.text) || ''}
                onChange={e => onChange({ text: e.target.value })}
            />
        )}
    </div>
);

const FullQuestionCard = ({ qNum, subParts, answers, onAnswerChange }) => (
    <article className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-heading"><h3>Question {qNum}</h3></div>
        {subParts.map(sp => (
            <SubPartInput key={sp.id} sp={sp} answer={answers[sp.id]} onChange={val => onAnswerChange(sp.id, val)} />
        ))}
    </article>
);

// Read-only, full-text preview for a not-yet-selected choice-group question —
// shows every sub-part in full so the student can actually judge which
// questions to pick, rather than a truncated single-sub-part snippet.
const QuestionPreview = ({ subParts }) => (
    <div>
        {subParts.map(sp => (
            <div key={sp.id} style={{ marginBottom: 10 }}>
                <div className="d-flex justify-content-between">
                    <strong style={{ fontSize: 13 }}>({sp.sub_part}) {sp.question_text}</strong>
                    <span className="empty-note" style={{ flexShrink: 0, marginLeft: 10 }}>{sp.marks} marks{sp.word_limit ? ` · ${sp.word_limit} words` : ''}</span>
                </div>
                {sp.options && (
                    <ul className="empty-note" style={{ marginTop: 4, marginBottom: 0 }}>
                        {Object.entries(sp.options).map(([key, text]) => <li key={key}>{key}. {text}</li>)}
                    </ul>
                )}
            </div>
        ))}
    </div>
);

const MockTestRunner = ({ slug }) => {
    const { testId, attemptId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [selectedByGroup, setSelectedByGroup] = useState({}); // { group_label: [question_number,...] }
    const [remainingSec, setRemainingSec] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const submittedRef = useRef(false);

    useEffect(() => {
        getMockTestQuestions(slug, testId).then(res => {
            setData(res);
            setRemainingSec(res.test.duration_min * 60);
        }).catch(() => setData(null));
    }, [slug, testId]);

    const handleAnswerChange = (subPartId, value) => {
        setAnswers(a => ({ ...a, [subPartId]: value }));
    };

    const toggleChoiceSelection = (groupLabel, qNum, attemptCount) => {
        setSelectedByGroup(prev => {
            const current = prev[groupLabel] || [];
            if (current.includes(qNum)) {
                return { ...prev, [groupLabel]: current.filter(n => n !== qNum) };
            }
            if (current.length >= attemptCount) return prev; // at cap — deselect one first
            return { ...prev, [groupLabel]: [...current, qNum] };
        });
    };

    const handleSubmit = useCallback(async () => {
        if (submittedRef.current) return;
        submittedRef.current = true;
        setSubmitting(true);
        try {
            await submitMockAttempt(slug, attemptId, answers);
            navigate(`/samkalp-ias/mock-tests/result/${attemptId}`);
        } catch {
            submittedRef.current = false;
            setSubmitting(false);
        }
    }, [slug, attemptId, answers, navigate]);

    useEffect(() => {
        if (remainingSec === null) return;
        if (remainingSec <= 0) { handleSubmit(); return; }
        const id = setTimeout(() => setRemainingSec(s => s - 1), 1000);
        return () => clearTimeout(id);
    }, [remainingSec, handleSubmit]);

    if (!data) return <div className="subpage"><p className="empty-note">Loading…</p></div>;

    const answeredCount = Object.values(answers).filter(v => v && (typeof v === 'string' ? v : v.text)).length;

    return (
        <div className="subpage">
            <div className="panel-heading" style={{ marginBottom: 16 }}>
                <h2>{data.test.title}</h2>
                <strong style={{ color: remainingSec < 60 ? '#c0392b' : 'var(--navy)' }}>⏱ {fmtTime(remainingSec)}</strong>
            </div>
            <p className="empty-note" style={{ marginBottom: 20 }}>{answeredCount} sub-part{answeredCount === 1 ? '' : 's'} answered</p>

            {data.compulsory.map(c => (
                <FullQuestionCard key={c.question_number} qNum={c.question_number} subParts={c.sub_parts} answers={answers} onAnswerChange={handleAnswerChange} />
            ))}

            {data.choice_groups.map(g => {
                const selected = selectedByGroup[g.group_label] || [];
                return (
                    <div key={g.group_label} style={{ marginBottom: 20 }}>
                        <p className="empty-note" style={{ fontWeight: 700 }}>
                            Attempt any {g.attempt_count} of these {g.of_count} questions — {selected.length}/{g.attempt_count} chosen
                        </p>
                        {g.questions.map(q => {
                            const isSelected = selected.includes(q.question_number);
                            if (isSelected) {
                                return (
                                    <div key={q.question_number} style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className="secondary-btn"
                                            style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                                            onClick={() => toggleChoiceSelection(g.group_label, q.question_number, g.attempt_count)}
                                        >
                                            ✕ Deselect
                                        </button>
                                        <FullQuestionCard qNum={q.question_number} subParts={q.sub_parts} answers={answers} onAnswerChange={handleAnswerChange} />
                                    </div>
                                );
                            }
                            return (
                                <article key={q.question_number} className="panel" style={{ marginBottom: 14, opacity: 0.75 }}>
                                    <div className="panel-heading">
                                        <h3>Question {q.question_number}</h3>
                                        <button
                                            type="button"
                                            className="primary-btn"
                                            disabled={selected.length >= g.attempt_count}
                                            onClick={() => toggleChoiceSelection(g.group_label, q.question_number, g.attempt_count)}
                                        >
                                            Select to Attempt
                                        </button>
                                    </div>
                                    <QuestionPreview subParts={q.sub_parts} />
                                </article>
                            );
                        })}
                    </div>
                );
            })}

            <button type="button" className="primary-btn" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Test'}
            </button>
        </div>
    );
};

export default MockTestRunner;
